import { useCallback } from 'react';
import {
  presign,
  uploadToS3,
  confirm,
  validateImageFile,
  type MediaCategory,
} from '@/lib/upload';

/**
 * EVER TV 본문(게시글 본문) 에디터 이미지 업로드용 presign 카테고리.
 * EVER TV 썸네일 업로드(EverTvFormPage ImageUploadField)와 동일하게 MISC 를 사용한다
 * — be-catalog 미디어 서비스에 EVER TV 전용 카테고리가 없어 기존 썸네일 경로와 정합을 맞춘다.
 */
const EVER_TV_IMAGE_CATEGORY: MediaCategory = 'MISC';

/**
 * RichTextEditor 의 onUploadImage 에 그대로 넘길 수 있는 업로드 함수를 반환한다.
 * presign → S3 직접 PUT → confirm 3단계를 거쳐 publicUrl 을 돌려준다.
 * 클라이언트 선검증 실패/네트워크 오류는 한국어 메시지로 throw 하므로
 * 호출부(RichTextEditor onUploadError)가 표면화한다.
 *
 * NoticeFormPage(useNoticeImageUpload) / EventEditPage(useEventImageUpload) 와 동일한
 * 3단계 패턴이며, EVER TV 본문 에디터에서 이미지 삽입을 위해 EverTvFormPage 가 사용한다.
 */
export const useEverTvImageUpload = (): ((file: File) => Promise<string>) => {
  return useCallback(async (file: File): Promise<string> => {
    const validationError = validateImageFile(file);
    if (validationError) {
      throw new Error(validationError);
    }

    const [slot] = await presign([{ file, category: EVER_TV_IMAGE_CATEGORY }]);
    if (!slot) {
      throw new Error('업로드 URL 발급 응답이 올바르지 않습니다.');
    }

    await uploadToS3([{ item: slot, file }]);

    const confirmed = await confirm([slot.fileId]);
    const publicUrl = confirmed.get(slot.fileId) ?? slot.publicUrl;
    if (!publicUrl) {
      throw new Error('업로드한 이미지 주소를 확인하지 못했습니다.');
    }
    return publicUrl;
  }, []);
};
